import { BlockToolConstructorOptions } from '@editorjs/editorjs/types/tools';
import EditorJS from '@editorjs/editorjs';
import { LocalTitleBlockTool } from '../_core/local-title-block/local-title-block.component';
import { REQUIRED_TUNE } from '../_core/required-block';
import { renderOpenToggle, renderWithBlockFocusWrapper, SECTION_OPEN_TOGGLE_CLASS } from '../_core/utils/editor.utils';
import { MutableRefObject } from 'react';
import { createRoot } from 'react-dom/client';
import { FormEditor } from '../../_pages/FormBuilderPage/form-editor.component';
import { IEditorToolsConfig, ISectionData, SectionType } from './section.types';
import { TEMPLATE_TYPE } from '../../../lib/types';
import { localTitleBlockExport, localTitleBlockImport } from '../_core/utils/block.utils';
import { IUploadBlockConfig } from '../upload';
import { SectionButtons } from './section-buttons';
import { BlockTitles, BlockType, CUSTOM_BLOCK_WRAPPER_CLASS, SCROLL_INTO_VIEW_CLASS } from '../_core/editor.const';
import { RenderWithLabelParams } from '../_core/label-block';
import { StatusIcon } from '../_core/status-icon.component';
import { sleep } from '../../../lib/utils';

const COMPLETED_BADGE_CLASS = 'completed-badge';
export const SECTION_WRAPPER_CLASS = 'ce-editorjs_section_wrapper';

interface ISectionBlockConfig extends IUploadBlockConfig {
  sectionType: SectionType;
  tools: IEditorToolsConfig;
}

/**
 * Section Block for EditorJS
 *
 * Portions of this block tool have been copied from https://github.com/calumk/editorjs-columns and is copyrighted by Calum Knott under the terms of the MIT license,
 * no specific license file was included in the source code at the time of attribution.
 * The files included here are a modification of the original source, and not distributed under the same license.
 */
export class Section extends LocalTitleBlockTool<ISectionData, ISectionBlockConfig> {
  editor: MutableRefObject<EditorJS | null | undefined>;
  private initialOpen: boolean;
  private openToggleId: string;
  private sectionWrapper?: HTMLDivElement;
  private sectionWrapperId: string;
  private sectionButtonsId: string;
  private blockIndex: number;
  private totalBlocks: number;
  private requestClosed: boolean;
  private requestSent: boolean;
  private isBaseSection: boolean;

  // NOTE: Section type toggling is currently hidden
  // private sectionTuneIndex?: number;
  // private questionGroupTune: TunesMenuConfigItem = {
  //   name: SectionType.QUESTION_GROUP,
  //   label: 'Question Group',
  //   icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><!--!Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M264.5 5.2c14.9-6.9 32.1-6.9 47 0l218.6 101c8.5 3.9 13.9 12.4 13.9 21.8s-5.4 17.9-13.9 21.8l-218.6 101c-14.9 6.9-32.1 6.9-47 0L45.9 149.8C37.4 145.8 32 137.3 32 128s5.4-17.9 13.9-21.8L264.5 5.2zM476.9 209.6l53.2 24.6c8.5 3.9 13.9 12.4 13.9 21.8s-5.4 17.9-13.9 21.8l-218.6 101c-14.9 6.9-32.1 6.9-47 0L45.9 277.8C37.4 273.8 32 265.3 32 256s5.4-17.9 13.9-21.8l53.2-24.6 152 70.2c23.4 10.8 50.4 10.8 73.8 0l152-70.2zm-152 198.2l152-70.2 53.2 24.6c8.5 3.9 13.9 12.4 13.9 21.8s-5.4 17.9-13.9 21.8l-218.6 101c-14.9 6.9-32.1 6.9-47 0L45.9 405.8C37.4 401.8 32 393.3 32 384s5.4-17.9 13.9-21.8l53.2-24.6 152 70.2c23.4 10.8 50.4 10.8 73.8 0z"/></svg>`,
  //   onActivate: (p) => this.toggleSectionTune(p),
  //   closeOnActivate: true
  // };

  // private sectionTune: TunesMenuConfigItem = {
  //   name: SectionType.SECTION,
  //   label: 'Section',
  //   icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M40 48C26.7 48 16 58.7 16 72v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V72c0-13.3-10.7-24-24-24H40zM192 64c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zM16 232v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V232c0-13.3-10.7-24-24-24H40c-13.3 0-24 10.7-24 24zM40 368c-13.3 0-24 10.7-24 24v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V392c0-13.3-10.7-24-24-24H40z"/></svg>',
  //   onActivate: (p) => this.toggleSectionTune(p),
  //   closeOnActivate: true
  // };

  constructor(
    props: BlockToolConstructorOptions<
      ISectionData & {
        forceOpen?: boolean;
        blockIndex?: number;
        totalBlocks?: number;
        requestClosed?: boolean;
        requestSent?: boolean;
        typeOverride?: SectionType;
      }
    >
  ) {
    const { config, data } = props;
    const propData = { ...data };
    delete propData.forceOpen;
    delete propData.blockIndex;
    delete propData.totalBlocks;
    delete propData.requestClosed;
    delete propData.requestSent;
    delete propData.typeOverride;

    super({
      ...props,
      config,
      data: {
        ...propData,
        outputData: data.outputData ?? {},
        type: data.typeOverride ?? config.sectionType,

        blockIndex: data.blockIndex,
        requestClosed: data.requestClosed,
        requestSent: data.requestSent,
        totalBlocks: data.totalBlocks
      }
    });

    this.editor = { current: undefined };
    this.isBaseSection = this.data.type === SectionType.SECTION;
    this.openToggleId = 'open-toggle-' + this.uid;
    this.sectionWrapperId = 'section-' + this.uid;
    this.sectionButtonsId = 'section-buttons-' + this.uid;
    this.blockIndex = data.blockIndex ?? 0;
    this.totalBlocks = data.totalBlocks ?? 0;
    this.requestClosed = !!data.requestClosed;
    this.requestSent = !!data.requestSent;

    this.initialOpen = (data.forceOpen || data.open) ?? !this.isBaseSection;
    this.data.open = this.initialOpen;

    this.templateType = this.isBaseSection ? TEMPLATE_TYPE.SECTION : TEMPLATE_TYPE.BLOCK;

    // Configure settings
    this.settings = this.settings.filter(({ name }) => name !== REQUIRED_TUNE);
  }

  static get enableLineBreaks() {
    return true;
  }

  static get isReadOnlySupported() {
    return true;
  }

  static get sanitize() {
    // Allo section data items to control their own HTML rules
    return { ...super.sanitize, outputData: true };
  }

  static get toolbox() {
    return {
      title: BlockTitles[BlockType.Section],
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="round" stroke-linejoin="round" d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125Z" /></svg>'
    };
  }

  updateData(data: ISectionData) {
    this.data = data;
  }

  private addWrapperBorder(wrapper?: HTMLElement | null, open?: boolean) {
    if (wrapper) {
      if (wrapper.closest('.ce-block')?.nextElementSibling ?? this.blockIndex < this.totalBlocks - 1) {
        wrapper.classList.toggle('border-y', open);
        wrapper.classList.toggle('border-t', !open);
      } else {
        wrapper.classList.toggle('border-y', true);
        wrapper.classList.toggle('border-t', false);
      }
    }
  }

  private renderOpenToggle({
    open,
    replace,
    toggleId = this.openToggleId,
    parent
  }: {
    open?: boolean;
    replace?: boolean;
    toggleId?: string;
    parent?: HTMLDivElement;
  }) {
    // Determine open status of toggle either by forced override, or by checking the class of the current toggle element, or using the initial value if that element doesn't exist yet
    let determinedOpen = open;
    if (determinedOpen === undefined) {
      const toggle = document.getElementById(toggleId);
      if (toggle) {
        const isToggleClosed = toggle.classList.contains('open-false');
        determinedOpen = isToggleClosed;
      }
    }
    this.data.open = determinedOpen ?? this.initialOpen;

    const foundParent = parent ?? (document.getElementById(this.wrapperId) as HTMLDivElement);
    const replaceSelector = replace ? '#' + (foundParent?.id ?? this.wrapperId) : undefined;

    if (this.isBaseSection || !this.readOnly())
      renderOpenToggle({
        append: !this.isBaseSection,
        dark: !this.isBaseSection,
        open: determinedOpen ?? this.initialOpen,
        openToggleId: toggleId,
        onOpenToggle: () => this.onOpenToggle({ toggleId, wrapper: foundParent }),
        parent: foundParent,
        replaceSelector
      });

    if (replace && this.isBaseSection) this.addWrapperBorder(document.getElementById(this.wrapperId), determinedOpen);
  }

  private onOpenToggle({
    forceOpen,
    toggleId = this.openToggleId,
    wrapper,
    wrapperId = this.wrapperId
  }: {
    forceOpen?: boolean;
    toggleId?: string;
    wrapper?: HTMLDivElement;
    wrapperId?: string;
  }) {
    let targetSectionContentId = this.sectionWrapperId;

    const finalWrapper = wrapper ?? document.getElementById(wrapperId);
    if (finalWrapper) {
      if (finalWrapper.nextSibling) targetSectionContentId = (finalWrapper.nextSibling as HTMLDivElement).id;
    }

    const sectionWrapper = document.getElementById(targetSectionContentId) as HTMLDivElement;
    sectionWrapper?.classList.toggle('hidden', forceOpen ? false : undefined);

    // Skip rendering open toggle when triggering open toggle for another wrapper (eg. don't change your toggle when updating your sibling section)
    this.renderOpenToggle({ open: forceOpen, replace: true, toggleId, parent: wrapper });
  }

  private readOnly() {
    return this.config.readOnly || this.api.readOnly.isEnabled;
  }

  renderSectionContent() {
    // Setup section content
    const content = document.createElement('div');
    content.classList.add(SECTION_WRAPPER_CLASS, 'w-full');
    if (!this.initialOpen) content.classList.add('hidden');
    content.id = this.sectionWrapperId;

    // astops the double paste issue
    content.addEventListener(
      'paste',
      (event) => {
        event.stopPropagation();
      },
      true
    );

    // Create nested editor view
    const editorContent = document.createElement('div');
    editorContent.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
      } else if (event.key === 'Tab' && !this.readOnly()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
      }
    });

    this.editor.current?.destroy();
    this.editor.current = null;

    // TODO: Create/destroy nested editor based on shown toggle
    const root = createRoot(editorContent);
    root.render(
      <FormEditor
        nested
        baseActiveSection={this.config.baseActiveSection}
        canQuickCreate={this.config.canQuickCreate || !!this.config.templateId}
        data={this.data.outputData}
        disabled={this.config.disabled}
        editorRef={this.editor}
        editorblock={content.id + '-editor'}
        isPrintView={this.config.isPrintView}
        onCreateTemplate={({ data }) => {
          if (this.config.onCreateTemplate) this.config.onCreateTemplate({ data, type: TEMPLATE_TYPE.BLOCK });
        }}
        onSave={this.config.onSave}
        readOnly={this.readOnly()}
        requestId={this.config.requestId}
        templateId={this.config.templateId}
        token={this.config.requestToken}
        type="section"
      />
    );

    content.appendChild(editorContent);

    // Add completion and navigation buttons to base sections
    if (this.isBaseSection) content.appendChild(this.renderSectionButtons());

    return content;
  }

  // A flag that will allow us to quickly determine what buttons are currently shown in the section buttons
  private getSectionNavButtonsConfig() {
    const prevBlock = this.blockIndex - 1 >= 0 ? this.blockIndex - 1 : null;
    const nextBlock = this.blockIndex + 1 < this.totalBlocks ? this.blockIndex + 1 : null;

    let navButtonsClass: 'has-prev-and-next' | 'has-prev' | 'has-next' | 'only-child' = 'only-child';
    if (prevBlock !== null && nextBlock !== null) navButtonsClass = 'has-prev-and-next';
    else if (prevBlock !== null) navButtonsClass = 'has-prev';
    else if (nextBlock !== null) navButtonsClass = 'has-next';

    return { prevBlock, nextBlock, navButtonsClass };
  }

  private renderSectionButtons() {
    const { prevBlock, nextBlock, navButtonsClass } = this.getSectionNavButtonsConfig();

    const sectionButtons = document.createElement('div');
    sectionButtons.id = this.sectionButtonsId;
    if (navButtonsClass) sectionButtons.classList.add(navButtonsClass);

    const buttonsRoot = createRoot(sectionButtons);

    buttonsRoot.render(
      <SectionButtons
        canComplete={this.requestSent && !this.requestClosed}
        prevBlock={prevBlock}
        nextBlock={nextBlock}
        completed={!!this.data.completed}
        uid={this.uid}
        getData={() => {
          const blockWrapper = document.getElementById(this.wrapperId);
          if (!blockWrapper) return new Promise((res) => res(null));
          return this.save(blockWrapper as HTMLDivElement);
        }}
        navigateToBlock={(newIndex) => {
          const newBlock = this.api.blocks.getBlockByIndex(newIndex)?.holder;
          if (newBlock) {
            // Toggle current block closed
            this.onOpenToggle({});
            newBlock.focus();

            // Find next block and toggle it open
            const matchingElements = newBlock.getElementsByClassName(SECTION_OPEN_TOGGLE_CLASS);
            if (matchingElements.length) {
              const siblingToggle = matchingElements[0];
              const sibling = siblingToggle.closest('.' + CUSTOM_BLOCK_WRAPPER_CLASS);

              // Open sibling and update sibling open toggle
              if (sibling)
                this.onOpenToggle({ forceOpen: true, toggleId: siblingToggle.id, wrapper: sibling as HTMLDivElement });
            }
          }
        }}
        toggleCompleted={(completed, timestamp) => {
          this.data.completed = completed;

          // Update section badge display
          document.querySelector(`#${this.wrapperId} .${COMPLETED_BADGE_CLASS}`)?.classList.toggle('hidden');
          this.config.onSave({
            forceFetchLatestData: true,
            overrideTimestamp: timestamp
          });
        }}
      />
    );

    return sectionButtons;
  }

  render() {
    const renderSettings: RenderWithLabelParams = this.isBaseSection
      ? { readOnly: { className: 'ce-header', elementType: 'h4' }, maxWidth: null }
      : { readOnly: { elementType: 'span' }, maxWidth: 'max-w-[500px]' };
    this.renderWithLocalTitle(renderSettings);

    this.renderSectionVariant();
    this.renderOpenToggle({ open: this.data.open, parent: this.wrapper });
    this.wrapper?.classList.add('section-wrapper');
    if (this.isBaseSection) this.wrapper?.classList.add('sticky', 'top-[6.95rem]', 'z-30');

    // Setup block wrapper with label line, and section content beneath
    const blockWrapper = document.createElement('div');
    blockWrapper.classList.add(SCROLL_INTO_VIEW_CLASS, 'scroll-mt-[5rem]');

    if (!this.wrapper) this.wrapper = document.createElement('div');
    this.wrapper.classList.add('flex', 'gap-4');
    if (this.isBaseSection) this.addWrapperBorder(this.wrapper, this.initialOpen);

    // Add completed icon to section title
    const completedIcon = document.createElement('div');
    completedIcon.classList.add('ml-auto', COMPLETED_BADGE_CLASS);
    if (!this.data.completed) completedIcon.classList.add('hidden');

    const completedIconRoot = createRoot(completedIcon);
    completedIconRoot.render(
      <div className="py-1">
        <StatusIcon inline slim backgroundColor="bg-success">
          Complete
        </StatusIcon>
      </div>
    );
    this.wrapper.appendChild(completedIcon);

    // Finish constructing html elements
    blockWrapper.appendChild(this.wrapper);

    this.sectionWrapper = this.renderSectionContent();
    this.renderSectionVariant(this.data.type);

    if (this.sectionWrapper) blockWrapper.appendChild(this.sectionWrapper);

    return renderWithBlockFocusWrapper(blockWrapper);
  }

  async save(blockContent: HTMLDivElement): Promise<ISectionData> {
    let newData: ISectionData = { outputData: this.data.outputData };

    try {
      newData = await super.save(blockContent);

      try {
        newData.outputData = this.editor.current?.save ? await this.editor.current.save(true) : this.data.outputData;
      } catch (err) {
        console.error('Error saving section', this.uid, err, { blockContent, currData: this.data.outputData });
      }
    } catch (err) {
      console.error('Error saving section super content', this.uid, err, {
        blockContent,
        currData: this.data.outputData
      });
    }

    newData.open = this.data.open;
    newData.type = this.data.type;
    newData.completed = this.data.completed;
    return newData;
  }

  private renderSectionVariant(type: SectionType = this.data.type) {
    switch (type) {
      case SectionType.SECTION:
        this.wrapper?.classList.add('border-black');
        this.renderOpenToggle({ replace: true });
        break;
      case SectionType.QUESTION_GROUP:
        this.initialOpen = true;
        // this.wrapper?.classList.remove('bg-blue-100');
        // this.wrapper?.classList.remove('bg-slate-100');
        this.renderOpenToggle({ replace: true });
        document.getElementById(this.sectionWrapperId)?.classList.remove('hidden');
        break;
    }
  }

  static get conversionConfig() {
    return { export: localTitleBlockExport, import: localTitleBlockImport };
  }

  private updateSectionButtons({
    updateOtherBlocks,
    isAdd,
    isRemove,
    removedBlockId
  }: {
    updateOtherBlocks?: boolean;
    isAdd?: boolean;
    isRemove?: boolean;
    removedBlockId?: string;
  }) {
    if (this.isBaseSection && this.block?.id) {
      const blockTypes = this.api.blocks
        .getBlockTypes()
        .filter(
          ({ name, id }) => name === SectionType.SECTION && (removedBlockId === undefined || id !== removedBlockId)
        );
      const currBlockIndex = blockTypes.findIndex(({ id }) => this.block !== undefined && id === this.block.id);

      if (currBlockIndex > -1) {
        this.totalBlocks = this.api.blocks.getBlocksCount() - (removedBlockId !== undefined ? 1 : 0);
        this.blockIndex = currBlockIndex;
        const { navButtonsClass } = this.getSectionNavButtonsConfig();

        const sectionButtons = document.getElementById(this.sectionButtonsId);
        const oldClasses = sectionButtons ? [...sectionButtons.classList] : [];

        if (sectionButtons && !oldClasses.includes(navButtonsClass)) {
          const newSectionsButtons = this.renderSectionButtons();
          sectionButtons.replaceWith(newSectionsButtons);
        }

        // Update other blocks in editor, and account for changes in block counts
        if (updateOtherBlocks && (isRemove || isAdd)) {
          blockTypes.forEach((block, index) => {
            if (index !== currBlockIndex)
              this.api.blocks
                .getById(block.id)
                ?.call(
                  isRemove ? 'updateAfterRemoved' : 'updateAfterAdd',
                  isRemove ? { removedBlockId: this.block?.id } : undefined
                );
          });
        }
      }
    }
  }

  /**
   * Called after block content added to the page
   */
  rendered() {
    this.updateSectionButtons({ updateOtherBlocks: true, isAdd: true });
    super.rendered();
  }

  /**
   * Called each time block content is updated
   */
  updated() {
    this.updateSectionButtons({});
    super.updated();
  }

  updateAfterAdd() {
    this.updateSectionButtons({});
  }

  updateAfterRemoved({ removedBlockId }: { removedBlockId: string }) {
    this.updateSectionButtons({ removedBlockId });
  }

  /**
   * Called after block removed from the page but before instance is deleted
   */
  removed() {
    this.updateSectionButtons({ isRemove: true, updateOtherBlocks: true });
    super.removed();
  }

  /**
   * Called after block was moved
   */
  moved() {
    this.updateSectionButtons({ updateOtherBlocks: true });
    super.moved();
  }

  async isReady() {
    // If readonly and there are no blocks to show then editor will not be created and we an determine editor is ready immediately
    if (this.readOnly() && !this.data.outputData.blocks.length) return true;

    while (!this.editor.current) await sleep(100);

    await this.editor.current?.isReady;
    // console.log(
    //   { localTitle: this.currTitle, id: this.sectionWrapperId },
    //   ` - ${this.isBaseSection ? 'Section' : 'Question Group'} is ready`,
    //   { label: this.data.label }
    // );
    return true;
  }

  // Block Settings
  // NOTE: Section type toggling is currently hidden

  // renderSettings() {
  //   if (this.data.type === SectionType.SECTION && this.sectionTuneIndex)
  //     this.settings[this.sectionTuneIndex].isActive = true;
  //   if (this.data.type === SectionType.QUESTION_GROUP) this.settings[this.questionGroupTuneIndex].isActive = true;

  //   return this.settings;
  // }

  // private toggleSectionTune({ name }: PopoverItem) {
  //   if (name) {
  //     this.data.type = name as SectionType;

  //     const otherSectionTypeIndex = this.settings.findIndex(
  //       (t) => t.name === (this.data.type === SectionType.SECTION ? SectionType.QUESTION_GROUP : SectionType.SECTION)
  //     );
  //     this.settings[otherSectionTypeIndex].isActive = false;
  //     this.renderSectionVariant(name as SectionType);
  //   }
  // }

  public destroy() {
    this.editor.current?.destroy();
    this.editor.current = null;
  }
}

export class QuestionGroup extends Section {
  constructor({
    data,
    ...rest
  }: BlockToolConstructorOptions<
    ISectionData & { forceOpen?: boolean; blockIndex?: number; totalBlocks?: number; requestSent?: boolean }
  >) {
    super({ ...rest, data: { ...data, typeOverride: SectionType.QUESTION_GROUP } });
  }
  static get toolbox() {
    return { ...super.toolbox, title: 'Question Group' };
  }
}
